import {
  BOT_SEQUENCE_DEFAULT_BODY_LOCALE,
  BOT_SEQUENCE_STANDARD_DAYS,
  BotSequenceConfigStep,
  BotSequenceType,
  Channel,
  defaultCopyFor
} from "@swivl-mono/types";

import { SEQUENCE_TYPE_META } from "./sequenceTypeMeta";

/**
 * Pure helpers over a ladder draft (ENG-3216). The ladder cascades as ONE unit, and a
 * saved day's number is fixed (decision 37) — so these add, remove and edit days but never
 * renumber a saved one.
 */

export function mintStepKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function sortLadder(ladder: BotSequenceConfigStep[]): BotSequenceConfigStep[] {
  return [...ladder].sort((left, right) => left.offsetDays - right.offsetDays);
}

export type LadderEdit = { ok: true; ladder: BotSequenceConfigStep[] } | { ok: false; error: string };

/** What a brand-new day may switch on besides the text message (decision 52). */
export interface NewDayOptions {
  /** True when the FMS behind this scope can take tenant notes — the note is on by default then. */
  fmsNoteAllowed: boolean;
}

/**
 * A new day starts fully filled in (decisions 51/52): the text message on with the default
 * exact wording, the note in the FMS on where the FMS supports it, voice and email off. Voice
 * and email prefill their defaults the moment someone switches them on.
 */
export function newStep(offsetDays: number, type: BotSequenceType, options: NewDayOptions): BotSequenceConfigStep {
  const copy = defaultCopyFor(type, offsetDays);
  return {
    key: mintStepKey(),
    offsetDays,
    enabled: true,
    deliveries: [{ channel: Channel.SMS, content: { mode: "verbatim", bodies: { [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: copy.sms } } }],
    ...(options.fmsNoteAllowed ? { fmsNote: { enabled: true, text: copy.fmsNote } } : {})
  };
}

export function addDay(ladder: BotSequenceConfigStep[], offsetDays: number, type: BotSequenceType, options: NewDayOptions): LadderEdit {
  if (!Number.isInteger(offsetDays) || offsetDays < 0) {
    return { ok: false, error: "Enter a whole number of days, 0 or more." };
  }
  if (ladder.some((step) => step.offsetDays === offsetDays)) {
    return { ok: false, error: `Day ${offsetDays} already exists.` };
  }
  return { ok: true, ladder: sortLadder([...ladder, newStep(offsetDays, type, options)]) };
}

/** The standard starting days — 0, 3 and 5 — each fully filled in (Round 2, R4). */
export function seedStandardDays(type: BotSequenceType, options: NewDayOptions): BotSequenceConfigStep[] {
  return BOT_SEQUENCE_STANDARD_DAYS.map((offsetDays) => newStep(offsetDays, type, options));
}

export function removeDay(ladder: BotSequenceConfigStep[], key: string): BotSequenceConfigStep[] {
  return ladder.filter((step) => step.key !== key);
}

export function updateDay(ladder: BotSequenceConfigStep[], key: string, patch: Partial<BotSequenceConfigStep>): BotSequenceConfigStep[] {
  return sortLadder(ladder.map((step) => (step.key === key ? { ...step, ...patch } : step)));
}

/**
 * A deep copy for "Customize for this scope": same offsets, same keys (keys are UI identity
 * only; the cursor is offset-based, so tenants mid-sequence are unaffected).
 */
export function copyLadderForScope(ladder: BotSequenceConfigStep[]): BotSequenceConfigStep[] {
  return JSON.parse(JSON.stringify(ladder)) as BotSequenceConfigStep[];
}

/** The chips a collapsed day row shows. */
/**
 * The chips a day row shows. `startingScriptName` is the name of the day's starting script when it
 * is in force at this scope (ENG-3403); a slug that no longer resolves still shows a chip so the
 * row says one is set.
 */
export function summarizeDay(step: BotSequenceConfigStep, type: BotSequenceType, startingScriptName?: string): string[] {
  const chips: string[] = [];
  const deliveries = step.deliveries ?? [{ channel: Channel.SMS, content: { mode: "generated" } }];
  for (const delivery of deliveries) {
    if (delivery.channel === Channel.SMS) chips.push("SMS");
    if (delivery.channel === Channel.VOICE) {
      chips.push("Voice");
      if (delivery.voicemail?.enabled) chips.push("Voicemail");
    }
  }
  if (step.email?.enabled) chips.push("Email");
  if (step.fmsNote?.enabled) chips.push("FMS note");
  if (step.sendASAP && SEQUENCE_TYPE_META[type].offersSendImmediately) chips.push("Immediately");
  if (step.deferrable === false) chips.push("Always send");
  if (step.callScriptSlug) chips.push(startingScriptName ? `Script: ${startingScriptName}` : "Starting script");
  return chips;
}

/** Ids of the days in `saved` — those whose number is fixed in the editor. */
export function savedStepKeys(saved: BotSequenceConfigStep[] | null): Set<string> {
  return new Set((saved ?? []).map((step) => step.key));
}
